import { create } from 'zustand';
import ServiceCndt, { STATUS_LABEL, TIPO_LABEL } from '@/services/ServiceCndt';
import { notify } from '@/stores/useToast';
import { mensagemErro } from '@/utils/apiError';
import UtilsDate from '@/utils/UtilsDate';

type Any = any;

const fmtData = (v?: string) =>
  v ? new Date(`${String(v).slice(0, 10)}T00:00:00`).toLocaleDateString('pt-BR') : '';
// valor cru p/ o prefill de edição (o DTO do BFF espera ISO YYYY-MM-DD)
const isoDate = (v?: string) => (v ? String(v).slice(0, 10) : '');

interface Dashboard {
  quantidadeTotalMonitoradas: number;
  percentualTotalMonitoradas: number;
  quantidadeRegular: number;
  percentualRegular: number;
  quantidadeIrregular: number;
  percentualIrregular: number;
  // CON-5284: card no padrão da Federal (Regular = CND+CPEN, Indisponível separado).
  quantidadeIndisponivel: number;
  percentualIndisponivel: number;
}

interface Filtro {
  empregador: string;
  status: string;
}
const FILTRO_VAZIO: Filtro = { empregador: '', status: '' };

// CON-5188: cada coluna ordenável da tabela mapeia para o alias que o backend aceita
// (cndt/search com sort=<alias>,<asc|desc>). Só as colunas com alias entram como
// sortable — as demais não têm suporte de ordenação no BFF.
const SORT_ALIASES: Record<string, string> = {
  empresa: 'empresa',
  cnpj: 'cnpj',
  emissao: 'emissao',
  validade: 'validade',
  codigo: 'codigo',
  tipo: 'tipo',
  totalProcessos: 'totalProcessos',
};

interface State {
  lista: Any[];
  total: number;
  page: number;
  size: number;
  busca: string;
  filtro: Filtro;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  dashboard: Dashboard | null;
  carregando: boolean;
  sincronizando: boolean;
  exportando: boolean;
  carregar: () => Promise<void>;
  carregarDashboard: () => Promise<void>;
  setBusca: (v: string) => void;
  buscar: () => Promise<void>;
  setPage: (page: number) => Promise<void>;
  setSize: (size: number) => Promise<void>;
  setSort: (by: string, order: 'asc' | 'desc') => Promise<void>;
  aplicarFiltro: (f: Filtro) => Promise<void>;
  limparFiltro: () => Promise<void>;
  sincronizar: (cnpj: string) => Promise<void>;
  historico: Any[];
  salvar: (dto: Any) => Promise<boolean>;
  atualizar: (id: string, dto: Any) => Promise<boolean>;
  excluir: (id: string) => Promise<void>;
  carregarHistorico: (id: string) => Promise<void>;
  exportar: () => Promise<void>;
}

// item da API -> linha da tabela. Mantém `cnpj` CRU — a máscara é aplicada só no
// render da coluna (paridade CRF/Certidão Federal).
const normalizar = (c: Any) => ({
  id: c.id,
  empresa: c.razaoSocial ?? '',
  cnpj: c.cnpj ?? '',
  emissao: fmtData(c.dataEmissao),
  validade: fmtData(c.dataValidade),
  codigo: c.codigo ?? '',
  totalProcessos: c.totalProcessos ?? 0,
  situacao: STATUS_LABEL[c.status as keyof typeof STATUS_LABEL] ?? c.status ?? '',
  tipo: TIPO_LABEL[c.tipo as keyof typeof TIPO_LABEL] ?? c.tipo ?? '',
  arquivoCndId: c.arquivoCndId ?? '',
  // crus p/ edição (não exibidos na tabela)
  _status: c.status ?? 'REGULAR',
  _emissao: isoDate(c.dataEmissao),
  _validade: isoDate(c.dataValidade),
});

// corpo do /search a partir do estado. O BFF filtra por `empregador` (ilike em
// razao_social OU cnpj) e `status`. Precedência: empregador do filtro > busca livre.
const _body = (get: () => State) => {
  const { page, size, busca, filtro, sortBy, sortOrder } = get();
  const empregador = filtro.empregador || busca || undefined;
  const status = filtro.status || undefined;
  const filtroDTO = empregador || status ? { empregador, status } : undefined;
  const alias = SORT_ALIASES[sortBy];
  const sort = alias ? `${alias},${sortOrder}` : undefined;
  return { filtro: filtroDTO, page, size, ...(sort ? { sort } : {}) };
};

export const useCndtStore = create<State>((set, get) => ({
  lista: [],
  total: 0,
  page: 0,
  size: 50,
  busca: '',
  filtro: { ...FILTRO_VAZIO },
  sortBy: '',
  sortOrder: 'asc',
  dashboard: null,
  carregando: false,
  sincronizando: false,
  exportando: false,
  historico: [],

  async carregar() {
    set({ carregando: true });
    try {
      const { data } = await ServiceCndt.search(_body(get));
      set({ lista: (data?.items ?? []).map(normalizar), total: data?.total ?? 0 });
    } catch {
      set({ lista: [], total: 0 });
      notify.error('Erro ao carregar CNDTs.');
    } finally {
      set({ carregando: false });
    }
  },

  async carregarDashboard() {
    try {
      const { data } = await ServiceCndt.dashboard();
      set({ dashboard: data ?? null });
    } catch {
      set({ dashboard: null });
      notify.error('Erro ao carregar o resumo.');
    }
  },

  setBusca(v) {
    set({ busca: v });
  },

  async buscar() {
    set({ page: 0 });
    await get().carregar();
  },

  async setPage(page) {
    set({ page });
    await get().carregar();
  },

  async setSize(size) {
    set({ size, page: 0 });
    await get().carregar();
  },

  async setSort(by, order) {
    set({ sortBy: by, sortOrder: order, page: 0 });
    await get().carregar();
  },

  async aplicarFiltro(f) {
    set({ filtro: f, page: 0 });
    await get().carregar();
  },

  async limparFiltro() {
    set({ filtro: { ...FILTRO_VAZIO }, page: 0 });
    await get().carregar();
  },

  async sincronizar(cnpj) {
    if (!cnpj) {
      notify.error('Selecione um empregador.');
      return;
    }
    set({ sincronizando: true });
    try {
      await ServiceCndt.sincronizar(cnpj);
      notify.success('CNDT sincronizada.');
      await get().carregar();
      await get().carregarDashboard();
    } catch (err) {
      notify.error(mensagemErro(err, 'Erro ao sincronizar CNDT.'));
    } finally {
      set({ sincronizando: false });
    }
  },

  async salvar(dto) {
    try {
      await ServiceCndt.salvar(dto);
      await get().carregar();
      await get().carregarDashboard();
      notify.success('CNDT salva.');
      return true;
    } catch {
      notify.error('Erro ao salvar a CNDT.');
      return false;
    }
  },

  async atualizar(id, dto) {
    try {
      await ServiceCndt.atualizar(id, dto);
      await get().carregar();
      await get().carregarDashboard();
      notify.success('CNDT atualizada.');
      return true;
    } catch {
      notify.error('Erro ao atualizar a CNDT.');
      return false;
    }
  },

  async excluir(id) {
    try {
      await ServiceCndt.excluir(id);
      await get().carregar();
      await get().carregarDashboard();
      notify.success('CNDT excluída.');
    } catch {
      notify.error('Erro ao excluir a CNDT.');
    }
  },

  async carregarHistorico(id) {
    try {
      const { data } = await ServiceCndt.historico(id, { page: 0, size: 50 });
      // CON-5300: histórico com data em DD/MM/AAAA (emissão com hora -> DD/MM/AAAA HH:mm:ss).
      const historico = (data?.items ?? []).map((h: Any) => ({
        ...h,
        dataEmissao: UtilsDate.formatBR(h.dataEmissao, { comHora: true }),
        dataValidade: UtilsDate.formatBR(h.dataValidade),
      }));
      set({ historico });
    } catch {
      set({ historico: [] });
    }
  },

  async exportar() {
    set({ exportando: true });
    try {
      await ServiceCndt.exportData(_body(get));
      notify.success('Exportação em processamento. Acompanhe em Arquivos › Exportações.');
    } catch {
      notify.error('Não foi possível exportar as CNDTs.');
    } finally {
      set({ exportando: false });
    }
  },
}));
